import { useEffect, useState } from 'react'
import { type postError, type lookup, NewEnrollmentStatus, ProgramVerificationStatus, type EnrollmentUpdateInfo, type EnrollmentUpdateResponse, AccountType } from 'core/types'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { CircularProgress, Typography, Stack, type SelectChangeEvent, Box } from '@mui/material'
import S3Image from 'Components/S3Image'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { ProgramTracker } from 'Components/Enrollment/ProgramTracker'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import AlertMessage from 'Components/AlertMessage'
import { toast } from 'react-toastify'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { Modal } from 'Components/Modal'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import { useAccountId } from 'ContextProviders/CurrentAccount'

export interface ConfirmEnrollmentProps {
  enrollmentID: number
  action: ConfirmAction
  open: boolean
  onClose: (accepted: boolean) => void
}

export enum ConfirmAction {
  Confirm,
  Modify,
  Waitlist,
  Update
}

enum AcceptanceMode {
  NotVerified = 0,
  SessionFull = 1,
  CanEnroll = 2
}

export function ConfirmEnrollment (props: ConfirmEnrollmentProps): JSX.Element {
  const initialResponse: EnrollmentUpdateResponse = {
    enrollmentID: props.enrollmentID,
    facilityID: null,
    sessionID: null,
    deliveryScheduleID: null,
    waitlistImmediately: false,
    waitlistStartDate: null,
    enrollImmediately: false,
    enrollmentDate: null,
    startDate: null
  }
  const accountType = useAccountId()?.type
  const usrIsISD = accountType === AccountType.ISD || accountType === AccountType.INTAKE_USER
  const [isValid, setIsValid] = useState(true)
  const [data, setData] = useState<EnrollmentUpdateInfo | null>(null)
  const [acceptance, setAcceptance] = useState<EnrollmentUpdateResponse>(initialResponse)
  const [sessionList, setSessionList] = useState<lookup[]>([])
  const [acceptanceMode, setAcceptanceMode] = useState<AcceptanceMode | null>(null)
  const [newStatus, setNewStatus] = useState<NewEnrollmentStatus>(NewEnrollmentStatus.Waitlisted)
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/UpdateDetails?id=${props.enrollmentID}`, {})
      const record = response as EnrollmentUpdateInfo
      setData(record)
      if (record.verificationStatus !== ProgramVerificationStatus.Verified) {
        setAcceptanceMode(AcceptanceMode.NotVerified)
      }
      setAcceptance({ ...acceptance, facilityID: record.facilityID, sessionID: record.sessionID, deliveryScheduleID: record.deliveryScheduleID, waitlistStartDate: record.waitlistStartDate, enrollmentDate: record.enrollmentDate, startDate: record.startDate })
      if (record.currentStatus === NewEnrollmentStatus.Waitlisted || record.currentStatus === NewEnrollmentStatus.Enrolled || record.currentStatus === NewEnrollmentStatus.Started) {
        setNewStatus(record.currentStatus)
      } else {
        setNewStatus(NewEnrollmentStatus.Processing)
      }
    }
    void loadData()
  }, [props.enrollmentID])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/SessionList/GetSessionsByTimeFrame?timeFrameID=${data?.timeFrameID ?? ''}&facilityID=${acceptance.facilityID ?? 0}`, {})
      setSessionList(response)
    }
    void loadData()
  }, [acceptance.facilityID])

  const availabilityCalculated = (noCapLimit: boolean, enrolled: number, capacity: number): void => {
    if (data?.verificationStatus === ProgramVerificationStatus.Verified) {
      if (props.action === ConfirmAction.Update) {
        if (!noCapLimit && enrolled >= capacity && data.sessionID !== acceptance.sessionID) {
          setIsValid(false)
          setAcceptanceMode(AcceptanceMode.SessionFull)
        } else {
          setIsValid(true)
          setAcceptanceMode(AcceptanceMode.CanEnroll)
        }
      } else if (!noCapLimit && enrolled >= capacity) {
        setAcceptanceMode(AcceptanceMode.SessionFull)
        setNewStatus(NewEnrollmentStatus.Waitlisted)
        setAcceptance({ ...acceptance, waitlistImmediately: true, enrollImmediately: false })
        setIsValid(true)
      } else {
        setAcceptanceMode(AcceptanceMode.CanEnroll)
        setAcceptance({ ...acceptance, waitlistImmediately: false, enrollImmediately: true })
        setNewStatus(NewEnrollmentStatus.Enrolled)
        setIsValid(true)
      }
    }
  }

  const handleApprove = async (): Promise<void> => {
    const { response: rsp } = await sendPost('/NewEnrollment/ConfirmEnrollment', acceptance)
    const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
    if (result.success) {
      if (newStatus === NewEnrollmentStatus.Waitlisted) {
        toast.success('The enrollment was successfully wait-listed.')
      } else if (newStatus === NewEnrollmentStatus.Started) {
        toast.success('The enrollment was successfully started.')
      } else if (newStatus === NewEnrollmentStatus.Enrolled) {
        toast.success('The child was successfully enrolled.')
      }
      if (props.onClose != null) {
        props.onClose(true)
        refresh()
      }
    } else {
      const errors: Array<{ error: string }> = result.errors
      errors.forEach(e => {
        toast.error(e.error)
      })
    }
  }

  const dialogTitle = (): string => {
    if (props.action === ConfirmAction.Confirm) {
      return 'Confirm Enrollment'
    } else if (props.action === ConfirmAction.Modify || props.action === ConfirmAction.Update) {
      return 'Modify Session'
    } else {
      return 'Waitlist Enrollment'
    }
  }

  const buttonText = (): string => {
    if (props.action === ConfirmAction.Update) { return 'Modify Session' }
    if (newStatus === NewEnrollmentStatus.Enrolled || newStatus === NewEnrollmentStatus.Started) {
      return 'Confirm Enrollment'
    } else if (newStatus === NewEnrollmentStatus.Waitlisted) {
      return 'Add to Waitlist'
    } else {
      return 'Modify Session'
    }
  }

  const facilityChanged = async (e: SelectChangeEvent<number>): Promise<void> => {
    setAcceptance({ ...acceptance, facilityID: Number(e.target.value), sessionID: null })
  }

  if (data == null) {
    return <CircularProgress />
  } else {
    const dialogContent = (data.verificationStatus === ProgramVerificationStatus.NotVerified
      ? <AlertMessage type='error' message='This enrollment cannot be confirmed as the program has not been verified.' />
      : <Stack direction='column' spacing={2}>
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{data.serviceProviderName}</Typography>
        <Stack direction='row' spacing={1}>
          {data.serviceS3Key != null ? <S3Image style={{ width: '30px', height: '30px' }} imageKey={data.serviceS3Key} /> : <SchoolOutlinedIcon sx={{ width: '30px', height: '30px' }} />}
          <Typography>{data.serviceName}</Typography>
          <ProgramVerificationChip programName={data.programName} status={data.verificationStatus} showStatusName={true} />
        </Stack>
        <Box>
          <SelectWithLabel
            name='facilityID'
            labelContent={<Typography variant='subtitle2'>Facility</Typography>}
            value={acceptance.facilityID ?? undefined}
            options={data.facilityList}
            width='100%'
            onChange={facilityChanged}
          />
        </Box>
        <Box>
          <SelectWithLabel
            name='sessionID'
            labelContent={<Typography variant='subtitle2'>Session</Typography>}
            value={acceptance.sessionID ?? undefined}
            options={sessionList}
            width='100%'
            onChange={(e) => {
              if (e.target.value !== null) {
                setAcceptance({ ...acceptance, sessionID: Number(e.target.value) })
              }
            }}
          />
        </Box>
        <Box>
          <SelectWithLabel
            name='deliveryScheduleID'
            labelContent={<Typography variant='subtitle2'>Delivery Schedule</Typography>}
            includeNoneOption
            noneOptionText='(none)'
            value={acceptance.deliveryScheduleID ?? undefined}
            options={data.deliveryScheduleList}
            width='100%'
            onChange={(e) => {
              if (e.target.value !== null) {
                setAcceptance({ ...acceptance, deliveryScheduleID: Number(e.target.value) })
              }
            }}
          />
        </Box>
        {acceptance.sessionID != null && <ProgramTracker programId={data.isdSpProgramID} sessionId={acceptance.sessionID ?? 0} onDisplay={availabilityCalculated} />}
        {acceptanceMode === AcceptanceMode.SessionFull && <Box>
          <DatePickerWithLabel
            value={acceptance.waitlistStartDate}
            labelContent={<Typography variant='subtitle2'>Wait-listed Start Date</Typography>}
            name={'waitlistStartDate'}
            onChange={(newValue: Date | null) => {
              setAcceptance({ ...acceptance, waitlistStartDate: newValue, waitlistImmediately: newValue != null })
            }}
          />
        </Box>}
        {acceptanceMode === AcceptanceMode.CanEnroll && <Box>
          <DatePickerWithLabel
            value={acceptance.enrollmentDate}
            labelContent={<Typography variant='subtitle2'>Enrolled Date</Typography>}
            name='enrollmentDate'
            onChange={(newValue: Date | null) => {
              setAcceptance({ ...acceptance, enrollmentDate: newValue })
            }}
          />
        </Box>}
        {props.action === ConfirmAction.Update && data?.currentStatus.toString() === 'Started' && usrIsISD && (
          <DatePickerWithLabel
            value={acceptance.startDate}
            labelContent={<Typography variant='subtitle2'>Start Date</Typography>}
            name='startDate'
            onChange={(newValue: Date | null) => {
              setAcceptance({ ...acceptance, startDate: newValue })
            }}
          />

        )}
        {props.action === ConfirmAction.Update && !isValid && (
          <AlertMessage message={'This session is full, and you may not enter a started enrollment to the wait list.'} type={'error'} />
        )}
        {props.action !== ConfirmAction.Update && newStatus != null && <Stack direction='row' spacing={1}>
          <Typography>New Enrollment Status</Typography>
          <EnrollmentChip statusId={newStatus} />
        </Stack>}
      </Stack>
    )

    return <Modal
      maxWidth='xs'
      fullWidth={true}
      open={props.open}
      title={dialogTitle()}
      onConfirm={data.verificationStatus === ProgramVerificationStatus.NotVerified ? () => {} : handleApprove}
      confirmButtonText={buttonText()}
      onClose={() => {
        props.onClose(false)
      }}
      hideConfirmButton={!isValid}
      confirmationContent={dialogContent}
    />
  }
}
